/**
 * "Summary" tab inside the Diagnostics panel. Shows the ticket's core
 * environment/context fields with important ones visually highlighted so
 * devs can orient themselves at a glance.
 */
import { Box, Chip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { FeedbackTicketDetail } from '../../types/feedback';

interface Props { ticket: FeedbackTicketDetail; isAdmin?: boolean }

export const SummaryView = ({ ticket, isAdmin }: Props) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <HighlightedRow
        label="Page URL"
        value={ticket.PageUrl}
        accent={theme.palette.primary.main}
        mono
        important
      />
      <HighlightedRow
        label="User Role"
        value={ticket.SubmittedByRole}
        accent={theme.palette.secondary.main}
        important
        chip
      />
      <HighlightedRow
        label="App Version"
        value={ticket.AppVersion ? `${ticket.AppVersion}${ticket.GitCommit ? ` (${ticket.GitCommit})` : ''}` : null}
        accent={theme.palette.info.main}
        mono
        important
      />
      <HighlightedRow
        label="Browser / OS"
        value={ticket.BrowserInfo}
        accent={theme.palette.info.main}
      />
      <HighlightedRow
        label="Screen"
        value={ticket.ScreenResolution ? `${ticket.ScreenResolution} (viewport ${ticket.ViewportSize})` : null}
        mono
      />
      <HighlightedRow
        label="Connection"
        value={ticket.ConnectionType}
        accent={ticket.ConnectionType?.includes('slow') || ticket.ConnectionType?.includes('2g') ? theme.palette.warning.main : undefined}
      />
      <HighlightedRow label="Timezone" value={ticket.Timezone} />
      <HighlightedRow label="Locale" value={ticket.Locale} />
      <HighlightedRow label="Referrer" value={ticket.Referrer} mono />
      {isAdmin && (
        <HighlightedRow
          label="Submitted By"
          value={ticket.SubmittedByName || ticket.SubmittedByEmail}
          accent={theme.palette.primary.main}
        />
      )}
      <HighlightedRow
        label="Submitted"
        value={ticket.sysCreateDate ? new Date(ticket.sysCreateDate).toLocaleString() : null}
      />
    </Box>
  );
};

interface RowProps {
  label: string;
  value: string | null | undefined;
  accent?: string;
  mono?: boolean;
  important?: boolean;
  chip?: boolean;
}

const HighlightedRow = ({ label, value, accent, mono, important, chip }: RowProps) => {
  const theme = useTheme();
  if (!value) return null;

  return (
    <Box sx={{
      display: 'flex', gap: 1.5, alignItems: 'baseline',
      px: 1, py: 0.5, borderRadius: 0.75,
      bgcolor: important && accent ? alpha(accent, 0.06) : 'transparent',
      borderLeft: important && accent ? `3px solid ${accent}` : '3px solid transparent',
      transition: 'background-color 0.15s ease',
    }}>
      <Typography variant="caption" sx={{
        fontWeight: important ? 700 : 600,
        fontSize: '0.7rem',
        minWidth: 110,
        color: important && accent ? accent : theme.palette.text.secondary,
        flexShrink: 0,
      }}>
        {label}
      </Typography>
      {chip ? (
        <Chip label={value} size="small" sx={{
          height: 20, fontSize: '0.7rem', fontWeight: 600,
          bgcolor: accent ? alpha(accent, 0.12) : undefined,
          color: accent,
        }} />
      ) : (
        <Typography variant="caption" sx={{
          fontSize: important ? '0.78rem' : '0.72rem',
          fontWeight: important ? 600 : 400,
          fontFamily: mono ? 'monospace' : 'inherit',
          wordBreak: 'break-all',
          color: important && accent ? theme.palette.text.primary : theme.palette.text.primary,
        }}>
          {value}
        </Typography>
      )}
    </Box>
  );
};
